import React, { useEffect, useState, useContext } from "react";
import { useFormik } from "formik";
import moment from "moment";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import { toast } from "react-toastify";
import useSortableData from "../../hooks/useSortableData";
import CurrencyFormat from "react-currency-format";
import jsPDF from "jspdf";
import "jspdf-autotable";
import MenuButton from "../MenuButton/MenuButton";

export default function GetCashInHand() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const [loading, setLoading] = useState(false);
  const [cashInHandData, setCashInHandData] = useState([]);
  const [show, setShow] = useState(false);

  const date = new Date();
  const defaultValue = date.toLocaleDateString("en-CA");
  const dateSelected = moment(defaultValue).format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(dateSelected);
  const [endDate, setEndDate] = useState(dateSelected);

  const fetchCashInHandRecords = async (values) => {
    if (Date.parse(values.from_date) <= Date.parse(values.to_date)) {
      setLoading(true);
      try {
        setCashInHandData([]);
        const response = await UserServices.fetchCashInHand(branch_id, values);
        if (response.status === 200) {
          const data = response.data.data.map((item) => ({
            date: moment(item.date).format("YYYY-MM-DD"),
            sale_counter_amount: item.sale_counter || 0,
            cafe_amount: item.cafe || 0,
            bank_amount: item.bank || 0,
            other_amount: item.other || 0,
            cash_in_bank: item.cash_in_bank || 0,
            charity_balance: item.charity_balance || 0,
            total_amount: item.total_cash || 0,
            expense_amount: item.expense || 0,
            charity_amount: item.charity || 0,
            gst_amount: item.gst || 0,
            cash_in_safe_amount: item.cash_in_safe || 0,
            cash_in_hand_amount: item.cash_in_hand || 0,
            description: item.description || "",
          }));
          setCashInHandData(data);
          setShow(true);
        } else {
          toast.error("Failed to fetch cash in hand records", { autoClose: 3000 });
        }
      } catch (err) {
        console.error("Error fetching cash in hand records:", err.response?.data || err.message);
        toast.error("Error fetching cash in hand records: " + (err.response?.data?.message || err.message), { autoClose: 3000 });
      } finally {
        setLoading(false);
      }
    } else {
      toast.error("End date should be greater than or equal to start date!", { autoClose: 3000 });
    }
  };

  const generatePdf = () => {
    const doc = new jsPDF("p", "mm", "letter");
    const mainTable = document.querySelector(".main-table");
    const summaryTable = document.querySelector(".summary-table table"); // Target the inner table
    const tableOptions = {
      headStyles: { fillColor: [232, 0, 43], textColor: 255 },
      styles: { fontSize: 5 },
      didParseCell: function (data) {
        // Format numeric cells to match CurrencyFormat
        const value = parseFloat(data.cell.raw);
        if (!isNaN(value)) {
          data.cell.text = [`Rs ${value.toLocaleString('en-US')}/-`];
        }
      },
    };

    // Add main table to PDF
    doc.text("Cash In Hand Report", 10, 10);
    doc.autoTable({
      html: mainTable,
      startY: 15,
      ...tableOptions,
    });

    // Add summary table to PDF
    const finalY = doc.autoTable.previous.finalY || 15;
    doc.text("Summary", 10, finalY + 10); // Positioned on the left
    doc.autoTable({
      html: summaryTable,
      startY: finalY + 15,
      margin: { left: 10 },
      ...tableOptions,
    });

    doc.save("Cash_In_Hand_Report.pdf");
  };

  const CashInHandTable = ({ data }) => {
    const { items, requestSort, sortConfig } = useSortableData(data);
    const getClassNamesFor = (name) => sortConfig?.key === name ? sortConfig.direction : undefined;

    const toNumber = (value) => Number(String(value).replace(/[^0-9.-]+/g, "")) || 0;

    const totals = data.reduce(
      (acc, row) => ({
        sale_counter_amount: acc.sale_counter_amount + toNumber(row.sale_counter_amount),
        cafe_amount: acc.cafe_amount + toNumber(row.cafe_amount),
        bank_amount: acc.bank_amount + toNumber(row.bank_amount),
        other_amount: acc.other_amount + toNumber(row.other_amount),
        cash_in_bank: acc.cash_in_bank + toNumber(row.cash_in_bank),
        charity_balance: acc.charity_balance + toNumber(row.charity_balance),
        total_amount: acc.total_amount + toNumber(row.total_amount),
        expense_amount: acc.expense_amount + toNumber(row.expense_amount),
        charity_amount: acc.charity_amount + toNumber(row.charity_amount),
        gst_amount: acc.gst_amount + toNumber(row.gst_amount),
        cash_in_safe_amount: acc.cash_in_safe_amount + toNumber(row.cash_in_safe_amount),
        cash_in_hand_amount: acc.cash_in_hand_amount + toNumber(row.cash_in_hand_amount),
      }),
      {
        sale_counter_amount: 0,
        cafe_amount: 0,
        bank_amount: 0,
        total_amount: 0,
        other_amount: 0,
        cash_in_bank: 0,
        charity_balance: 0,
        expense_amount: 0,
        charity_amount: 0,
        gst_amount: 0,
        cash_in_safe_amount: 0,
        cash_in_hand_amount: 0,
      }
    );

    // Get the last entry based on date (latest in database)
    const lastEntry = data.length > 0 ? data.reduce((latest, current) => {
      return moment(latest.date).isAfter(moment(current.date)) ? latest : current;
    }) : null;

    return (
      <>
        <div className="col-md-12 mt-2">
          <div className="row">
            <div className="col-md-6">
              <h5 className="mb-0 text-uppercase">Cash In Hand Records</h5>
            </div>
            <div className="col-md-3" />
            <div className="col-md-3 text-end">
              <button
                className="btn btn-primary mx-2 file-download float-end mb-3"
                onClick={() => generatePdf()}
              >
                <i className="bi bi-file-earmark-pdf"></i> PDF
              </button>
            </div>
          </div>
          <hr />
        </div>
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-borderless table-style-1 main-table" data-caption={`Cash In Hand Report (${moment(values.from_date).format("DD-MM-YYYY")} - ${moment(values.to_date).format("DD-MM-YYYY")})`}>
                <thead>
                  <tr>
                    <th scope="col" className="no_sort">Sr#</th>
                    <th scope="col" onClick={() => requestSort("date")} className={getClassNamesFor("date")}>
                      Date
                    </th>
                    <th scope="col">Sale Counter Amount</th>
                    <th scope="col">Cafe Amount</th>
                    <th scope="col">Bank Amount</th>
                    {/* <th scope="col">Cash In Bank</th> */}
                    <th scope="col">Other Amount</th>
                    <th scope="col">Total Amount</th>
                    <th scope="col">Expense Amount</th>
                    <th scope="col">Charity Amount</th>
                    <th scope="col">GST Amount</th>
                    <th scope="col">Cash In Safe</th>
                    {/* <th scope="col">Charity Balance</th> */}
                    <th scope="col">Cash In Hand </th>
                    <th scope="col">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length ? items.map((el, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{moment(el.date).format("DD-MM-YYYY")}</td>
                      <td><CurrencyFormat value={toNumber(el.sale_counter_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      <td><CurrencyFormat value={toNumber(el.cafe_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      <td><CurrencyFormat value={toNumber(el.bank_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      {/* <td><CurrencyFormat value={toNumber(el.cash_in_bank)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td> */}
                      <td><CurrencyFormat value={toNumber(el.other_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      <td><CurrencyFormat value={toNumber(el.total_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      <td><CurrencyFormat value={toNumber(el.expense_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      <td><CurrencyFormat value={toNumber(el.charity_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      <td><CurrencyFormat value={toNumber(el.gst_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      <td><CurrencyFormat value={toNumber(el.cash_in_safe_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      {/* <td><CurrencyFormat value={toNumber(el.charity_balance)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td> */}
                      <td><CurrencyFormat value={toNumber(el.cash_in_hand_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      <td>{el.description}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="15" className="text-center text-capitalize">No record found</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: "#FFFF00", fontWeight: "bold" }}>
                    <td colSpan="2">Total</td>
                    <td><CurrencyFormat value={toNumber(totals.sale_counter_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    <td><CurrencyFormat value={toNumber(totals.cafe_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    <td><CurrencyFormat value={toNumber(totals.bank_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    {/* <td><CurrencyFormat value={toNumber(totals.cash_in_bank)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td> */}
                    <td><CurrencyFormat value={toNumber(totals.other_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    <td><CurrencyFormat value={toNumber(totals.total_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    <td><CurrencyFormat value={toNumber(totals.expense_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    <td><CurrencyFormat value={toNumber(totals.charity_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    <td><CurrencyFormat value={toNumber(totals.gst_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    <td><CurrencyFormat value={toNumber(totals.cash_in_safe_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    {/* <td><CurrencyFormat value={toNumber(totals.charity_balance)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td> */}
                    <td><CurrencyFormat value={toNumber(totals.cash_in_hand_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        {/* Mini Summary Table - Moved to right bottom */}
        {lastEntry && (
          <div className="col-md-6 offset-md-6 mt-3">
            <div className="card">
              <div className="card-body">
                <h5 className="mb-0 text-uppercase">Summary of {moment(lastEntry.date).format("DD-MM-YYYY")}</h5>
                <hr />
                <div className="table-responsive summary-table">
                  <table className="table table-borderless table-style-1">
                    <tbody>
                      <tr>
                        <td>Cash In Hand</td>
                        <td><CurrencyFormat value={toNumber(lastEntry.cash_in_hand_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      </tr>
                      <tr>
                        <td>Cash In Safe</td>
                        <td><CurrencyFormat value={toNumber(lastEntry.cash_in_safe_amount)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      </tr>
                      <tr>
                        <td>Cash In Bank</td>
                        <td><CurrencyFormat value={toNumber(lastEntry.cash_in_bank)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      </tr>
                      <tr>
                        <td>Charity Balance</td>
                        <td><CurrencyFormat value={toNumber(lastEntry.charity_balance)} displayType="text" thousandSeparator prefix="Rs " suffix="/-" /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } = useFormik({
    enableReinitialize: true,
    initialValues: { from_date: startDate, to_date: endDate },
    onSubmit: fetchCashInHandRecords,
  });

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            <div className="card-header py-3">
              <h5 className="mb-0 font-20">Get Cash In Hand Records</h5>
            </div>
            <div className="card-body">
              <form className="row g-3" onSubmit={handleSubmit}>
                <div className="link-hirarcy">
                  <span className="text-danger">Reports</span> » Cash In Hand
                </div>
                <div className="col-12">
                  <h5>Dates</h5>
                </div>
                <div className="col-12 col-md-4">
                  <div className="form-group custom-group">
                    <label className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      placeholder="Enter From Date"
                      name="from_date"
                      value={values.from_date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors.from_date && touched.from_date && <p className="text-danger">{errors.from_date}</p>}
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="form-group custom-group">
                    <label className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      placeholder="Enter To Date"
                      name="to_date"
                      value={values.to_date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors.to_date && touched.to_date && <p className="text-danger">{errors.to_date}</p>}
                  </div>
                </div>
                <div className="col-12 col-md-4 col-lg-2 offset-md-4 offset-lg-5">
                  <button
                    type="submit"
                    className="btn btn-primary px-5 rounded-0 w-100"
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Go"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          {show && <CashInHandTable data={cashInHandData} />}
        </div>
      </main>
      <div className={`loader ${loading ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>
    </div>
  );
}
